import type { Orientation } from '@/types/orientation.types';

/**
 * Insert /landscape/ subfolder before the filename in a local path
 * e.g. /images/products/black-dog-tag/frame.png -> /images/products/black-dog-tag/landscape/frame.png
 */
function toLandscapePath(portraitPath: string): string {
  const lastSlash = portraitPath.lastIndexOf('/');
  return portraitPath.slice(0, lastSlash) + '/landscape' + portraitPath.slice(lastSlash);
}

/**
 * Insert a_90/ transformation after /upload/ in a Cloudinary URL
 */
function toLandscapeCloudinaryUrl(url: string): string {
  return url.replace('/upload/', '/upload/a_90/');
}

/**
 * Transform an external URL to its landscape variant, or return null if unavailable
 */
function toLandscapeExternalUrl(url: string): string | null {
  if (url.includes('cloudinary.com')) {
    return toLandscapeCloudinaryUrl(url);
  }
  // ImgBB and other URLs not yet available in landscape
  return null;
}

/**
 * Maps product IDs to their frame overlay image paths
 * Frame overlays are used to composite AI-generated designs
 * Images served from /public/images/products/
 */
export const FRAME_OVERLAYS: Record<string, string> = {
  'black-dog-tag': '/images/products/black-dog-tag/black-dog-tag-white-frame.png',
  'black-card': '/images/products/black-card/black-card-white-frame.png',
  'black-wallet': '/images/products/black-wallet/black-wallet-white-frame.png',
};

/**
 * Get the frame overlay path for a product ID
 */
export function getFrameOverlay(productId: string, orientation: Orientation = 'portrait'): string {
  const framePath = FRAME_OVERLAYS[productId];
  if (!framePath) {
    throw new Error(`No frame overlay found for product: ${productId}`);
  }
  return orientation === 'landscape' ? toLandscapePath(framePath) : framePath;
}

/**
 * Product image variants for different template/toggle combinations
 * Local paths for preview display
 */
export interface ProductImageVariant {
  standard: string;
  qrMiddle: string;
  qrBottom: string;
}

/**
 * Map of product IDs to their image variants (local paths)
 * Used for canvas BASE LAYER display (product images centered on 1024x1536 canvas)
 *
 * Image dimensions vary per product (e.g., dog tag: 576x992, wallet: 512x815).
 * The frame overlay (1024x1536) is handled separately via frontFrameOverlay.
 */
const PRODUCT_IMAGE_VARIANTS: Record<string, ProductImageVariant> = {
  'black-card': {
    standard: '/images/products/black-card/black-card-product-cutout.png',
    qrMiddle: '/images/products/black-card/black-card-product-cutout.png',
    qrBottom: '/images/products/black-card/black-card-product-cutout.png',
  },
  'black-wallet': {
    standard: '/images/products/black-wallet/black-wallet-product-cutout.png',
    qrMiddle: '/images/products/black-wallet/black-wallet-product-cutout.png',
    qrBottom: '/images/products/black-wallet/black-wallet-product-cutout.png',
  },
  'black-dog-tag': {
    standard: '/images/products/black-dog-tag/black-dog-tag-base-image.png',
    qrMiddle: '/images/products/black-dog-tag/black-dog-tag-qr-code-center.png',
    qrBottom: '/images/products/black-dog-tag/black-dog-tag-qr-code-bottom.png',
  },
};

/**
 * External URLs for Fal.ai (ImgBB hosted)
 * These are publicly accessible URLs sent to the AI service
 */
const EXTERNAL_IMAGE_URLS: Record<string, Partial<ProductImageVariant>> = {
  'black-card': {
    standard:
      'https://res.cloudinary.com/dxf6qtgxg/image/upload/v1772766513/engraving-products/black-card-base-image-v2-cutout.png',
  },
  'black-wallet': {
    standard:
      'https://res.cloudinary.com/dxf6qtgxg/image/upload/v1772765537/engraving-products/black-wallet-base-image-v9-cutout.png',
  },
  'black-dog-tag': {
    standard:
      'https://res.cloudinary.com/dxf6qtgxg/image/upload/v1773618334/engraving-products/black-dog-tag-base-image-v3.png',
    qrMiddle:
      'https://res.cloudinary.com/dxf6qtgxg/image/upload/v1773692164/engraving-products/black-dog-tag-qr-code-center.png',
    qrBottom:
      'https://res.cloudinary.com/dxf6qtgxg/image/upload/v1773692172/engraving-products/black-dog-tag-qr-code-bottom.png',
  },
};

/**
 * Map template IDs that use QR variants
 */
const QR_TEMPLATE_MAPPING: Record<string, 'qrMiddle' | 'qrBottom'> = {
  'qr-code-centered': 'qrMiddle',
  'qr-code-bottom': 'qrBottom',
};

/**
 * Resolve the correct local product image path based on template and toggles
 * Used for canvas preview display
 */
export function resolveProductImage(
  productId: string,
  templateId: string | null,
  orientation: Orientation = 'portrait'
): string {
  const variants = PRODUCT_IMAGE_VARIANTS[productId];
  if (!variants) {
    // Fallback to standard frame
    const fallbackPath = FRAME_OVERLAYS[productId] || `/images/products/${productId}-frame.png`;
    return orientation === 'landscape' ? toLandscapePath(fallbackPath) : fallbackPath;
  }

  // Check if template requires QR variant
  const qrVariant = templateId ? QR_TEMPLATE_MAPPING[templateId] : null;

  let portraitPath: string;
  if (qrVariant === 'qrMiddle') {
    portraitPath = variants.qrMiddle;
  } else if (qrVariant === 'qrBottom') {
    portraitPath = variants.qrBottom;
  } else {
    portraitPath = variants.standard;
  }

  return orientation === 'landscape' ? toLandscapePath(portraitPath) : portraitPath;
}

/**
 * Get the external URL for a product image variant
 * Used for Fal.ai API calls (needs publicly accessible URL)
 */
export function getExternalImageUrl(
  productId: string,
  templateId: string | null,
  orientation: Orientation = 'portrait'
): string | null {
  const urls = EXTERNAL_IMAGE_URLS[productId];
  if (!urls) {
    return null;
  }

  // Check if template requires QR variant
  const qrVariant = templateId ? QR_TEMPLATE_MAPPING[templateId] : null;

  let portraitUrl: string | null | undefined;
  if (qrVariant === 'qrMiddle') {
    portraitUrl = urls.qrMiddle;
  } else if (qrVariant === 'qrBottom') {
    portraitUrl = urls.qrBottom;
  } else {
    portraitUrl = urls.standard;
  }

  if (!portraitUrl) return null;
  return orientation === 'landscape' ? toLandscapeExternalUrl(portraitUrl) : portraitUrl;
}
